import type { Map, SymbolLayerSpecification } from "@iventis/mapbox-gl";
import type { Feature, Point } from "geojson";
import type { MapObjectProperties } from "@iventis/map-types/src/map-object-properties";
import type { BehaviorSubject } from "rxjs";
import { TextStyle } from "@iventis/domain-model/model/textStyle";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { getLayerStyleTextProperties, isTextStyleType } from "@iventis/layer-style-helpers/src/text-style-helpers";
import { Layer } from "../generic-layers/layer";
import type { MapModuleLayer, MapState } from "../../types/store-schema";
import type { StylePropertyToValueMapV2 } from "../../types/style-types";
import { getDefaultStyleProperty } from "../../utilities/style-helpers";
import { styleValueToMapboxStyleValue, textContentValueToMapboxStyleValue } from "../mapbox/engine-mapbox-helpers";
import { ApplyListItemTextFunction, boldFontStack, regularFontStack } from "../mapbox/engine-mapbox-types-and-constants";
import { IMapboxLayerManager, MapboxLayerManager } from "./mapbox-layer-manager";
import { DataFieldListItemStore } from "../../data-store/data-field-list-item-store";

export type TextLayerSource = Feature<Point, MapObjectProperties>[];

export interface MapboxTextLayerEvents {
    getAboveLayerId: (layerId: string) => string;
    applyTextFieldExpression?: ApplyListItemTextFunction;
}

/**
 * A mapbox symbol layer that only handles text rendering concerns: creating the layer/source,
 * updating text style properties, filters and lifecycle. It has no knowledge of how the text
 * is positioned relative to the map object (e.g. models, icons); callers can supply extra
 * paint/layout properties for that behaviour.
 */
export class MapboxTextLayer extends Layer<TextLayerSource, TextStyle> {
    public readonly layerId: string;

    protected readonly sourceId: string;

    private readonly mapboxLayerManager: IMapboxLayerManager<TextLayerSource>;

    private readonly metadataType: string;

    constructor(
        protected readonly map: Map,
        private readonly layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        private readonly dataFieldListItemStore: DataFieldListItemStore,
        private readonly events: MapboxTextLayerEvents,
        options: { layerIdSuffix: string; metadataType: string }
    ) {
        super(state$);
        this.layerId = `${layer.id}-${options.layerIdSuffix}`;
        this.sourceId = this.layerId;
        this.metadataType = options.metadataType;
        this.mapboxLayerManager = new MapboxLayerManager(map, this.layerId);
    }

    public addLayer(): void {
        this.mapboxLayerManager.createLocalLayerClass();
        this.mapboxLayerManager.addLocalSource();
        this.mapboxLayerManager.addLayerToMapbox(this.iventisLayerToMapboxTextLayer(this.getIventisLayer()), {
            layerSourceType: "local",
            aboveLayerId: this.events.getAboveLayerId(this.layer.id),
        });
        this.updateMapLevel(this.state$.value.currentLevel);
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.addDateFilter(this.layer.dataFields, dateFilter.day, dateFilter.time);
        }
    }

    public removeLayer(): void {
        this.mapboxLayerManager.removeLayer();
    }

    public addLocalSource(): void {
        this.mapboxLayerManager.addLocalSource();
    }

    public updateLocalSource(source: TextLayerSource): void {
        this.mapboxLayerManager.updateLocalSource(source);
    }

    public removeLocalSource(): void {
        this.mapboxLayerManager.removeLocalSource();
    }

    public updateStyle(styleChanges: StylePropertyToValueMapV2<TextStyle>): void {
        if (styleChanges.textColour) {
            this.mapboxLayerManager.setPaintProperty("text-color", styleValueToMapboxStyleValue(styleChanges.textColour));
        }
        if (styleChanges.textOutlineColour) {
            this.mapboxLayerManager.setPaintProperty("text-halo-color", styleValueToMapboxStyleValue(styleChanges.textOutlineColour));
        }
        if (styleChanges.textOutlineWidth) {
            this.mapboxLayerManager.setPaintProperty("text-halo-width", styleValueToMapboxStyleValue(styleChanges.textOutlineWidth));
        }
        if (styleChanges.textOpacity) {
            this.mapboxLayerManager.setPaintProperty("text-opacity", styleValueToMapboxStyleValue(styleChanges.textOpacity));
        }
        if (styleChanges.textOverlap) {
            this.mapboxLayerManager.setLayoutProperty("text-allow-overlap", styleValueToMapboxStyleValue(styleChanges.textOverlap));
        }
        if (styleChanges.textSize) {
            this.mapboxLayerManager.setLayoutProperty("text-size", styleValueToMapboxStyleValue(styleChanges.textSize));
        }
        if (styleChanges.textBold) {
            this.mapboxLayerManager.setLayoutProperty("text-font", styleValueToMapboxStyleValue(styleChanges.textBold) ? boldFontStack : regularFontStack);
        }
        if (styleChanges.text || styleChanges.textContent) {
            this.mapboxLayerManager.setLayoutProperty("text-field", this.createTextFieldValue(this.getIventisLayer()));
        }
    }

    public hideLayer(): void {
        this.mapboxLayerManager.hideLayer();
    }

    public showLayer(): void {
        this.mapboxLayerManager.showLayer();
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.mapboxLayerManager.updateMapOrder(aboveLayerId);
    }

    public updateMapLevel(level: number): void {
        this.mapboxLayerManager.updateMapLevel(level);
    }

    public addDateFilter(dataFields: DataField[], day: number, time: number): void {
        this.mapboxLayerManager.addDateFilter(dataFields, day, time);
    }

    public removeDateFilter(): void {
        this.mapboxLayerManager.removeDateFilter();
    }

    public getLayerIdsForSnapping(): string[] {
        return [];
    }

    /**
     * Sets a paint property on the underlying mapbox symbol layer. Exposed for callers that
     * need to update non-text paint properties (e.g. model elevation) after the layer is added.
     */
    public setPaintProperty<K extends keyof SymbolLayerSpecification["paint"]>(name: K, value: SymbolLayerSpecification["paint"][K]): void {
        this.mapboxLayerManager.setPaintProperty(name, value);
    }

    /** Sets a layout property on the underlying mapbox symbol layer. */
    public setLayoutProperty<K extends keyof SymbolLayerSpecification["layout"]>(name: K, value: SymbolLayerSpecification["layout"][K]): void {
        this.mapboxLayerManager.setLayoutProperty(name, value);
    }

    private hasFeatureValue(dataFieldId: string, source: TextLayerSource): boolean {
        return source.some((feature) => feature?.properties?.[dataFieldId] != null);
    }

    private async getTextContentListItems(dataFieldId: string, source: TextLayerSource): Promise<DataFieldListItem[]> {
        const listItems = await this.dataFieldListItemStore.getDataFieldListItems(dataFieldId);

        if (listItems.length > 0 || this.hasFeatureValue(dataFieldId, source)) {
            return listItems;
        }

        return [{ id: "", dataFieldId, name: "" } as DataFieldListItem];
    }

    protected iventisLayerToMapboxTextLayer(iventisLayer: MapModuleLayer): SymbolLayerSpecification {
        if (!isTextStyleType(iventisLayer.styleType)) {
            throw new Error(`Layer ${iventisLayer.id} does not have a text style.`);
        }

        const textStyle = getLayerStyleTextProperties(iventisLayer);
        const { styleType } = iventisLayer;
        return {
            id: this.layerId,
            type: "symbol",
            paint: {
                "text-color": getStaticStyleValue(textStyle.textColour ?? getDefaultStyleProperty(iventisLayer.styleType, "textColour")),
                "text-halo-color": getStaticStyleValue(textStyle.textOutlineColour ?? getDefaultStyleProperty(styleType, "textOutlineColour")),
                "text-halo-width": getStaticStyleValue(textStyle.textOutlineWidth ?? getDefaultStyleProperty(styleType, "textOutlineWidth")),
                "text-opacity": getStaticStyleValue(textStyle.textOpacity ?? getDefaultStyleProperty(styleType, "textOpacity")),
            },
            layout: {
                visibility: iventisLayer.visible ? "visible" : "none",
                "text-allow-overlap": getStaticStyleValue(textStyle.textOverlap ?? getDefaultStyleProperty(styleType, "textOverlap")),
                "text-field": this.createTextFieldValue(iventisLayer),
                "text-size": styleValueToMapboxStyleValue(textStyle.textSize ?? getDefaultStyleProperty(styleType, "textSize")),
                "text-font": getStaticStyleValue(textStyle.textBold ?? getDefaultStyleProperty(styleType, "textBold")) ? boldFontStack : regularFontStack,
            },
            source: this.sourceId,
            metadata: {
                type: this.metadataType,
                name: iventisLayer.name,
            },
        };
    }

    private createTextFieldValue(iventisLayer: MapModuleLayer) {
        if (!isTextStyleType(iventisLayer.styleType)) {
            throw new Error(`Layer ${iventisLayer.id} does not have a text style.`);
        }

        const style = getLayerStyleTextProperties(iventisLayer);
        const { styleType } = iventisLayer;
        if (!getStaticStyleValue(style.text ?? getDefaultStyleProperty(styleType, "text"))) {
            return "";
        }

        const source = this.getGeoJson();

        return textContentValueToMapboxStyleValue(
            style.textContent ?? getDefaultStyleProperty(styleType, "textContent"),
            {
                projectDataFields: this.state$.value.projectDataFields,
                unitOfMeasurement: this.state$.value.unitOfMeasurement.value,
            },
            this.layerId,
            iventisLayer.id,
            this.events.applyTextFieldExpression,
            (dataFieldId) => this.getTextContentListItems(dataFieldId, source)
        );
    }
}
